import {
  filter,
  firstValueFrom,
  timeout,
  Observable,
  Subject,
  Subscription,
  of,
} from 'rxjs';
import {createContext, useContext, ReactNode} from 'react';

export interface InlineDataEvent {
  name: string;
  data?: Record<string, unknown>;
  nonce?: string;
  code?: number;
  error?: string;
}

export class InlineDataEventBus {
  private subject = new Subject<InlineDataEvent>();

  get observable(): Observable<InlineDataEvent> {
    return this.subject.asObservable();
  }

  subscribe(listener: (event: InlineDataEvent) => void): Subscription {
    return this.subject.subscribe(listener);
  }

  emit(event: InlineDataEvent): void {
    this.subject.next(event);
  }

  emitWithResult(
    event: InlineDataEvent & {nonce: string},
    options: {timeoutMs: number} = {timeoutMs: 5000},
  ): Promise<InlineDataEvent> {
    const resultEventName = `${event.name}-result`;

    const result = firstValueFrom(
      this.observable.pipe(
        filter((e) => e.name === resultEventName && e.nonce === event.nonce),
        timeout({
          each: options.timeoutMs,
          with: () =>
            of({
              name: resultEventName,
              nonce: event.nonce,
              error: 'timeout',
            }),
        }),
      ),
    );

    this.emit(event);

    return result;
  }
}

const defaultBus = new InlineDataEventBus();

const EventContext = createContext<InlineDataEventBus>(defaultBus);

type Props = {
  children: ReactNode;
  value?: InlineDataEventBus;
};

export function InlineDataEventBusProvider({
  children,
  value = defaultBus,
}: Props) {
  return (
    <EventContext.Provider value={value}>{children}</EventContext.Provider>
  );
}

export function useInlineDataEvents(): InlineDataEventBus {
  return useContext(EventContext);
}
